import React, { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { useToast } from "../hooks/use-toast";
import { useLanguage } from "../contexts/LanguageContext";
import { Mail, CheckCircle } from "lucide-react";
import { useSpamSchutz, Honigtopf } from "./SpamSchutz";

const NewsletterSection = () => {
  const { t } = useLanguage();
  const { honigtopf, spamFelder } = useSpamSchutz();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    consent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleConsentChange = (checked) => {
    setFormData(prev => ({
      ...prev,
      consent: checked
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.lastName || !formData.email) {
      toast({
        title: t('newsletter.fillFields'),
        description: t('newsletter.fillFieldsDesc'),
        variant: "destructive"
      });
      return;
    }

    if (!formData.consent) {
      toast({
        title: t('newsletter.consentRequired'),
        description: t('newsletter.consentRequiredDesc'),
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Send newsletter subscription to backend
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...(await spamFelder()),
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          source: 'Homepage Newsletter',
          timestamp: new Date().toISOString()
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        toast({
          title: t('newsletter.success'),
          description: t('newsletter.successDesc'),
        });

        // Reset form
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          consent: false
        });
      } else {
        throw new Error(result.message || 'Subscription failed');
      }
    } catch (error) {
      toast({
        title: t('newsletter.error'),
        description: error.message || t('newsletter.errorDesc'),
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 md:py-28 bg-surface">
      <div className="container mx-auto px-4">
        <div className="max-w-[62ch]">
          {/* Header */}
          <div className="max-w-[64ch] mb-14">
            <div className="inline-flex items-center gap-2 bg-brand/10 px-4 py-2 rounded-full mb-4">
              <Mail className="h-4 w-4 text-brand-400" />
              <span className="text-brand-300 text-sm font-medium">{t('newsletter.badge')}</span>
            </div>
            
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-ink mb-5">
              {t('newsletter.title')}
            </h2>
            
            <p className="text-[1.05rem] leading-relaxed text-ink-muted">
              {t('newsletter.subtitle')}
            </p>
          </div>

          {/* Newsletter Form */}
          <div className="bg-surface-raised border border-hairline rounded-xl p-6 md:p-8">
            <form onSubmit={handleSubmit} className="space-y-4">
              <Honigtopf {...honigtopf} />
              {/* Name Fields */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-ink-muted mb-1.5">
                    {t('newsletter.firstName')} *
                  </label>
                  <Input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder={t('newsletter.firstName')}
                    className="w-full bg-surface border-hairline focus:border-brand focus:ring-brand text-sm"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-ink-muted mb-1.5">
                    {t('newsletter.lastName')} *
                  </label>
                  <Input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder={t('newsletter.lastName')}
                    className="w-full bg-surface border-hairline focus:border-brand focus:ring-brand text-sm"
                    required
                  />
                </div>
              </div>

              {/* Email Field */}
              <div>
                <label className="block text-xs font-medium text-ink-muted mb-1.5">
                  {t('newsletter.email')} *
                </label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="ihre.email@unternehmen.de"
                  className="w-full bg-surface border-hairline focus:border-brand focus:ring-brand text-sm"
                  required
                />
              </div>

              {/* Consent Checkbox */}
              <div className="flex items-start gap-2">
                <Checkbox
                  id="consent"
                  checked={formData.consent}
                  onCheckedChange={handleConsentChange}
                  className="mt-0.5"
                />
                <label htmlFor="consent" className="text-xs text-ink-muted leading-relaxed">
                  {t('newsletter.consent')}{" "}
                  <a 
                    href="/privacy" 
                    className="text-brand-400 hover:text-brand-300 underline font-medium"
                  >
                    {t('newsletter.privacy')}
                  </a>
                </label>
              </div>

              {/* Submit Button */}
              <div className="flex justify-center">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-brand hover:bg-brand-600 text-ink-onBrand px-8 py-2.5 font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
                      {t('newsletter.submitting')}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4" />
                      {t('newsletter.subscribe')}
                    </div>
                  )}
                </Button>
              </div>
            </form>

            {/* Benefits */}
            <div className="mt-6 pt-6 border-t border-brand-400/25">
              <div className="grid md:grid-cols-3 gap-4 text-center">
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 bg-brand/15 rounded-full flex items-center justify-center mb-2">
                    <Mail className="h-5 w-5 text-brand-400" />
                  </div>
                  <span className="text-xs text-ink-muted font-medium">{t('newsletter.benefit1')}</span>
                </div>
                
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 bg-brand/15 rounded-full flex items-center justify-center mb-2">
                    <CheckCircle className="h-5 w-5 text-brand-400" />
                  </div>
                  <span className="text-xs text-ink-muted font-medium">{t('newsletter.benefit2')}</span>
                </div>
                
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 bg-brand/15 rounded-full flex items-center justify-center mb-2">
                    <CheckCircle className="h-5 w-5 text-brand-400" />
                  </div>
                  <span className="text-xs text-ink-muted font-medium">{t('newsletter.benefit3')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewsletterSection;